import React, { useState, useEffect } from 'react';
import { Plus, Trash2, GripVertical, Save, Eye, ExternalLink, Copy, Check, Palette, Globe, ChevronUp, ChevronDown, Type, Image, MessageSquareQuote, HelpCircle, DollarSign, Video, Layout, Users, Star, LayoutGrid, Code, FileCode, Menu, Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Sidebar from '../components/Sidebar';
import MediaSelector from '../components/MediaSelector';
import BlueskyIcon from '../components/icons/BlueskyIcon';
import api from '../utils/api';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

// Block type definitions
const blockTypes = [
  { type: 'text', name: 'Text Section', icon: Type, description: 'Add a text block with title and content' },
  { type: 'image', name: 'Image', icon: Image, description: 'Add an image with optional caption' },
  { type: 'gallery', name: 'Image Gallery', icon: LayoutGrid, description: 'Display multiple images in a grid' },
  { type: 'testimonials', name: 'Testimonials', icon: MessageSquareQuote, description: 'Show customer testimonials' },
  { type: 'faq', name: 'FAQ', icon: HelpCircle, description: 'Frequently asked questions section' },
  { type: 'pricing', name: 'Pricing Table', icon: DollarSign, description: 'Display pricing plans' },
  { type: 'video', name: 'Video Embed', icon: Video, description: 'Embed a YouTube or Vimeo video' },
  { type: 'cta', name: 'Call to Action', icon: Layout, description: 'Add a prominent CTA section' },
  { type: 'team', name: 'Team Members', icon: Users, description: 'Showcase your team' },
  { type: 'stats', name: 'Statistics', icon: Star, description: 'Display key numbers and stats' },
  { type: 'html', name: 'Custom HTML', icon: Code, description: 'Add raw HTML/CSS code for full design control' },
];

// Default block templates
const getDefaultBlock = (type) => {
  const base = { id: uuidv4(), type, enabled: true };
  
  switch (type) {
    case 'text':
      return { ...base, title: 'Section Title', content: 'Add your content here...', alignment: 'center' };
    case 'image':
      return { ...base, url: '', alt: '', caption: '', width: 'full' };
    case 'gallery':
      return { ...base, title: 'Gallery', images: [{ id: uuidv4(), url: '', alt: '' }], columns: 3 };
    case 'testimonials':
      return { 
        ...base, 
        title: 'What Our Customers Say',
        items: [{ id: uuidv4(), quote: 'Amazing service!', author: 'John Doe', role: 'CEO', avatar: '' }]
      };
    case 'faq':
      return { 
        ...base, 
        title: 'Frequently Asked Questions',
        items: [{ id: uuidv4(), question: 'What is your service?', answer: 'We provide...' }]
      };
    case 'pricing':
      return { 
        ...base, 
        title: 'Pricing Plans',
        items: [
          { id: uuidv4(), name: 'Basic', price: '$9', period: '/month', features: ['Feature 1', 'Feature 2'], highlighted: false },
          { id: uuidv4(), name: 'Pro', price: '$29', period: '/month', features: ['Everything in Basic', 'Feature 3', 'Feature 4'], highlighted: true },
        ]
      };
    case 'video':
      return { ...base, title: '', url: '', type: 'youtube' };
    case 'cta':
      return { ...base, title: 'Ready to Get Started?', subtitle: 'Join thousands of satisfied customers', button_text: 'Start Now', button_url: '#contact', style: 'primary' };
    case 'team':
      return { 
        ...base, 
        title: 'Meet Our Team',
        members: [{ id: uuidv4(), name: 'Team Member', role: 'Position', image: '', bio: '' }]
      };
    case 'stats':
      return { 
        ...base, 
        title: '',
        items: [
          { id: uuidv4(), value: '100+', label: 'Customers' },
          { id: uuidv4(), value: '50+', label: 'Projects' },
          { id: uuidv4(), value: '99%', label: 'Satisfaction' },
        ]
      };
    case 'html':
      return { 
        ...base, 
        html: `<div style="padding: 60px 20px; text-align: center;">
  <h2 style="font-size: 2rem; margin-bottom: 1rem;">Custom HTML Section</h2>
  <p style="color: #666;">Paste your HTML and CSS here for full design control.</p>
</div>`,
        css: '',
        full_width: true
      };
    default:
      return base;
  }
};

const defaultLandingPage = {
  enabled: false,
  use_html_mode: false, // New: Full HTML mode toggle
  custom_html: '', // New: Full page custom HTML
  custom_css: '', // New: Full page custom CSS
  title: 'Welcome',
  hero: {
    headline: 'Build Something Amazing',
    subheadline: 'Your one-page website to showcase your brand, product, or service.',
    image_url: '',
    image_position: 'right', // 'left', 'right', 'background', 'none'
    cta_text: 'Get Started',
    cta_url: '#contact',
    background_color: '#4F46E5',
    text_color: '#ffffff'
  },
  about: {
    enabled: true,
    title: 'About Us',
    content: 'Tell your story here. What makes you unique? Why should visitors care?',
    image_url: ''
  },
  features: {
    enabled: true,
    title: 'What We Offer',
    items: [
      { id: uuidv4(), title: 'Feature 1', description: 'Describe your first key feature or benefit.', icon: 'star' },
      { id: uuidv4(), title: 'Feature 2', description: 'Describe your second key feature or benefit.', icon: 'zap' },
      { id: uuidv4(), title: 'Feature 3', description: 'Describe your third key feature or benefit.', icon: 'shield' }
    ]
  },
  blocks: [], // Custom blocks array
  contact: {
    enabled: true,
    title: 'Get in Touch',
    email: '',
    phone: '',
    address: '',
    show_form: true
  },
  // Navigation settings
  navigation: {
    enabled: false,
    menu_id: '',
    sticky: true,
    transparent: false,
    logo_url: '',
    logo_text: ''
  },
  // Enhanced footer settings
  footer: {
    enabled: true,
    style: 'simple', // 'simple', 'columns', 'centered'
    logo_url: '',
    company_name: '',
    tagline: '',
    copyright: '© 2024 Your Company. All rights reserved.',
    columns: [
      { id: uuidv4(), title: 'Company', links: [] },
      { id: uuidv4(), title: 'Resources', links: [] }
    ],
    social_links: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
      youtube: '',
      bluesky: ''
    },
    contact_info: {
      email: '',
      phone: '',
      address: ''
    },
    background_color: '',
    text_color: ''
  },
  theme: {
    primary_color: '#4F46E5',
    secondary_color: '#7C3AED',
    background_color: '#ffffff',
    text_color: '#1e293b'
  }
};

const TenantLandingPage = () => {
  const [landingPage, setLandingPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAddBlockModal, setShowAddBlockModal] = useState(false);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [menus, setMenus] = useState([]);

  const generatePreviewUrl = (tenantId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/site/${tenantId}`;
  };

  useEffect(() => {
    fetchLandingPage();
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    try {
      const res = await api.get('/navigation');
      setMenus(res.data || []);
    } catch (error) {
      console.error('Failed to fetch menus:', error);
      setMenus([]);
    }
  };

  const fetchLandingPage = async () => {
    try {
      const res = await api.get('/tenant/landing-page');
      const data = res.data || defaultLandingPage;
      // Ensure blocks array exists
      if (!data.blocks) data.blocks = [];
      // Ensure navigation exists
      if (!data.navigation) data.navigation = defaultLandingPage.navigation;
      // Ensure footer has new structure
      if (!data.footer || !data.footer.style) {
        data.footer = { ...defaultLandingPage.footer, ...data.footer };
      }
      setLandingPage(data);
      if (res.data?.tenant_id) {
        setPreviewUrl(generatePreviewUrl(res.data.tenant_id));
      }
    } catch (error) {
      setLandingPage(defaultLandingPage);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/tenant/landing-page', landingPage);
      toast.success('Landing page saved successfully!');
      if (res.data?.tenant_id) {
        setPreviewUrl(generatePreviewUrl(res.data.tenant_id));
      }
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const copyPreviewUrl = () => {
    if (previewUrl) {
      navigator.clipboard.writeText(previewUrl);
      setCopied(true);
      toast.success('URL copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Block management functions
  const addBlock = (type) => {
    const newBlock = getDefaultBlock(type);
    setLandingPage(prev => ({
      ...prev,
      blocks: [...(prev.blocks || []), newBlock]
    }));
    setShowAddBlockModal(false);
    setActiveBlockId(newBlock.id);
    toast.success(`${blockTypes.find(b => b.type === type)?.name} block added`);
  };

  const removeBlock = (blockId) => {
    setLandingPage(prev => ({
      ...prev,
      blocks: prev.blocks.filter(b => b.id !== blockId)
    }));
    if (activeBlockId === blockId) setActiveBlockId(null);
    toast.success('Block removed');
  };

  const moveBlock = (blockId, direction) => {
    setLandingPage(prev => {
      const blocks = [...prev.blocks];
      const index = blocks.findIndex(b => b.id === blockId);
      if (direction === 'up' && index > 0) {
        [blocks[index - 1], blocks[index]] = [blocks[index], blocks[index - 1]];
      } else if (direction === 'down' && index < blocks.length - 1) {
        [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
      }
      return { ...prev, blocks };
    });
  };

  const updateBlock = (blockId, updates) => {
    setLandingPage(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.id === blockId ? { ...b, ...updates } : b)
    }));
  };

  const updateHero = (field, value) => {
    setLandingPage(prev => ({ ...prev, hero: { ...prev.hero, [field]: value } }));
  };

  const updateAbout = (field, value) => {
    setLandingPage(prev => ({ ...prev, about: { ...prev.about, [field]: value } }));
  };

  const updateFeatures = (field, value) => {
    setLandingPage(prev => ({ ...prev, features: { ...prev.features, [field]: value } }));
  };

  const updateFeatureItem = (id, field, value) => {
    setLandingPage(prev => ({
      ...prev,
      features: {
        ...prev.features,
        items: prev.features.items.map(item => item.id === id ? { ...item, [field]: value } : item)
      }
    }));
  };

  const addFeatureItem = () => {
    setLandingPage(prev => ({
      ...prev,
      features: {
        ...prev.features,
        items: [...prev.features.items, { id: uuidv4(), title: 'New Feature', description: 'Describe this feature...', icon: 'star' }]
      }
    }));
  };

  const removeFeatureItem = (id) => {
    setLandingPage(prev => ({
      ...prev,
      features: { ...prev.features, items: prev.features.items.filter(item => item.id !== id) }
    }));
  };

  const updateContact = (field, value) => {
    setLandingPage(prev => ({ ...prev, contact: { ...prev.contact, [field]: value } }));
  };

  const updateTheme = (field, value) => {
    setLandingPage(prev => ({ ...prev, theme: { ...prev.theme, [field]: value } }));
  };

  const updateFooter = (field, value) => {
    setLandingPage(prev => ({ ...prev, footer: { ...prev.footer, [field]: value } }));
  };

  const updateNavigation = (field, value) => {
    setLandingPage(prev => ({ ...prev, navigation: { ...prev.navigation, [field]: value } }));
  };

  const updateFooterSocialLinks = (platform, value) => {
    setLandingPage(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        social_links: { ...prev.footer.social_links, [platform]: value }
      }
    }));
  };

  const updateFooterContactInfo = (field, value) => {
    setLandingPage(prev => ({
      ...prev,
      footer: {
        ...prev.footer,
        contact_info: { ...prev.footer.contact_info, [field]: value }
      }
    }));
  };

  // Block Editor Components
  const renderBlockEditor = (block) => {
    const BlockIcon = blockTypes.find(b => b.type === block.type)?.icon || Layout;
    
    return (
      <Card key={block.id} className={`mb-4 ${activeBlockId === block.id ? 'ring-2 ring-indigo-500' : ''}`}>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <GripVertical size={16} className="text-slate-400 cursor-grab" />
              <BlockIcon size={18} className="text-indigo-600" />
              <span className="font-medium">{blockTypes.find(b => b.type === block.type)?.name}</span>
            </div>
            <div className="flex items-center gap-1">
              <Switch
                checked={block.enabled}
                onCheckedChange={(checked) => updateBlock(block.id, { enabled: checked })}
              />
              <Button variant="ghost" size="sm" onClick={() => moveBlock(block.id, 'up')}>
                <ChevronUp size={16} />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => moveBlock(block.id, 'down')}>
                <ChevronDown size={16} />
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => removeBlock(block.id)}>
                <Trash2 size={16} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {renderBlockFields(block)}
        </CardContent>
      </Card>
    );
  };

  const renderBlockFields = (block) => {
    switch (block.type) {
      case 'text':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Content</Label>
              <Textarea value={block.content} onChange={(e) => updateBlock(block.id, { content: e.target.value })} rows={4} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Alignment</Label>
              <Select value={block.alignment} onValueChange={(v) => updateBlock(block.id, { alignment: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'image':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Image</Label>
              <MediaSelector
                value={block.url || ''}
                onChange={(url) => updateBlock(block.id, { url })}
                label="Block Image"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Alt Text</Label>
              <Input value={block.alt} onChange={(e) => updateBlock(block.id, { alt: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Caption</Label>
              <Input value={block.caption} onChange={(e) => updateBlock(block.id, { caption: e.target.value })} />
            </div>
          </div>
        );

      case 'gallery':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Columns</Label>
              <Select value={String(block.columns)} onValueChange={(v) => updateBlock(block.id, { columns: parseInt(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 Columns</SelectItem>
                  <SelectItem value="3">3 Columns</SelectItem>
                  <SelectItem value="4">4 Columns</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">Images</Label>
                <Button variant="outline" size="sm" onClick={() => updateBlock(block.id, { images: [...block.images, { id: uuidv4(), url: '', alt: '' }] })}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              {block.images.map((img, idx) => (
                <div key={img.id} className="p-3 border rounded-lg bg-slate-50 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">Image {idx + 1}</span>
                    <Button variant="ghost" size="sm" className="text-red-500 h-6" onClick={() => updateBlock(block.id, { images: block.images.filter(i => i.id !== img.id) })}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <MediaSelector
                    value={img.url || ''}
                    onChange={(url) => {
                      const images = [...block.images];
                      images[idx] = { ...img, url };
                      updateBlock(block.id, { images });
                    }}
                    label="Gallery Image"
                  />
                </div>
              ))}
            </div>
          </div>
        );

      case 'testimonials':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Section Title</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">Testimonials</Label>
                <Button variant="outline" size="sm" onClick={() => updateBlock(block.id, { items: [...block.items, { id: uuidv4(), quote: '', author: '', role: '', avatar: '' }] })}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              {block.items.map((item, idx) => (
                <div key={item.id} className="p-3 border rounded-lg space-y-2 bg-slate-50">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-500">Testimonial {idx + 1}</span>
                    <Button variant="ghost" size="sm" className="text-red-500 h-6" onClick={() => updateBlock(block.id, { items: block.items.filter(i => i.id !== item.id) })}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <Textarea value={item.quote} onChange={(e) => {
                    const items = [...block.items];
                    items[idx] = { ...item, quote: e.target.value };
                    updateBlock(block.id, { items });
                  }} placeholder="Quote" rows={2} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={item.author} onChange={(e) => {
                      const items = [...block.items];
                      items[idx] = { ...item, author: e.target.value };
                      updateBlock(block.id, { items });
                    }} placeholder="Author name" />
                    <Input value={item.role} onChange={(e) => {
                      const items = [...block.items];
                      items[idx] = { ...item, role: e.target.value };
                      updateBlock(block.id, { items });
                    }} placeholder="Role/Company" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Section Title</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">Questions</Label>
                <Button variant="outline" size="sm" onClick={() => updateBlock(block.id, { items: [...block.items, { id: uuidv4(), question: '', answer: '' }] })}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              {block.items.map((item, idx) => (
                <div key={item.id} className="p-3 border rounded-lg space-y-2 bg-slate-50">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-500">FAQ {idx + 1}</span>
                    <Button variant="ghost" size="sm" className="text-red-500 h-6" onClick={() => updateBlock(block.id, { items: block.items.filter(i => i.id !== item.id) })}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <Input value={item.question} onChange={(e) => {
                    const items = [...block.items];
                    items[idx] = { ...item, question: e.target.value };
                    updateBlock(block.id, { items });
                  }} placeholder="Question" />
                  <Textarea value={item.answer} onChange={(e) => {
                    const items = [...block.items];
                    items[idx] = { ...item, answer: e.target.value };
                    updateBlock(block.id, { items });
                  }} placeholder="Answer" rows={2} />
                </div>
              ))}
            </div>
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Section Title</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">Pricing Plans</Label>
                <Button variant="outline" size="sm" onClick={() => updateBlock(block.id, { items: [...block.items, { id: uuidv4(), name: 'Plan', price: '$0', period: '/month', features: ['Feature'], highlighted: false, button_text: 'Get Started', button_url: '#contact' }] })}>
                  <Plus size={14} className="mr-1" /> Add Plan
                </Button>
              </div>
              {block.items.map((item, idx) => (
                <div key={item.id} className={`p-3 border rounded-lg space-y-2 ${item.highlighted ? 'bg-indigo-50 border-indigo-300' : 'bg-slate-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">Plan {idx + 1}</span>
                    <div className="flex items-center gap-2">
                      <label className="text-xs flex items-center gap-1">
                        <input type="checkbox" checked={item.highlighted} onChange={(e) => {
                          const items = [...block.items];
                          items[idx] = { ...item, highlighted: e.target.checked };
                          updateBlock(block.id, { items });
                        }} />
                        Featured
                      </label>
                      <Button variant="ghost" size="sm" className="text-red-500 h-6" onClick={() => updateBlock(block.id, { items: block.items.filter(i => i.id !== item.id) })}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input value={item.name} onChange={(e) => {
                      const items = [...block.items];
                      items[idx] = { ...item, name: e.target.value };
                      updateBlock(block.id, { items });
                    }} placeholder="Plan name" />
                    <Input value={item.price} onChange={(e) => {
                      const items = [...block.items];
                      items[idx] = { ...item, price: e.target.value };
                      updateBlock(block.id, { items });
                    }} placeholder="$29" />
                    <Input value={item.period} onChange={(e) => {
                      const items = [...block.items];
                      items[idx] = { ...item, period: e.target.value };
                      updateBlock(block.id, { items });
                    }} placeholder="/month" />
                  </div>
                  <Textarea value={item.features.join('\n')} onChange={(e) => {
                    const items = [...block.items];
                    items[idx] = { ...item, features: e.target.value.split('\n') };
                    updateBlock(block.id, { items });
                  }} placeholder="Features (one per line)" rows={3} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={item.button_text || ''} onChange={(e) => {
                      const items = [...block.items];
                      items[idx] = { ...item, button_text: e.target.value };
                      updateBlock(block.id, { items });
                    }} placeholder="Button text (e.g. Get Started)" />
                    <Input value={item.button_url || ''} onChange={(e) => {
                      const items = [...block.items];
                      items[idx] = { ...item, button_url: e.target.value };
                      updateBlock(block.id, { items });
                    }} placeholder="https://..., /signup or #contact" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-slate-500">Stripe Checkout / Payment Link (optional)</Label>
                    <Input
                      value={item.checkout_url || ''}
                      onChange={(e) => {
                        const items = [...block.items];
                        items[idx] = { ...item, checkout_url: e.target.value };
                        updateBlock(block.id, { items });
                      }}
                      placeholder="https://buy.stripe.com/..."
                      data-testid={`tenant-pricing-checkout-url-${item.id}`}
                    />
                    <p className="text-[11px] text-slate-400">Paste a Stripe Payment Link from your own Stripe dashboard. When set, this button opens checkout in a new tab (overrides the Button URL above).</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'video':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title (optional)</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Video URL</Label>
              <Input value={block.url} onChange={(e) => updateBlock(block.id, { url: e.target.value })} placeholder="YouTube or Vimeo URL" />
            </div>
          </div>
        );

      case 'cta':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Subtitle</Label>
              <Input value={block.subtitle} onChange={(e) => updateBlock(block.id, { subtitle: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Button Text</Label>
                <Input value={block.button_text} onChange={(e) => updateBlock(block.id, { button_text: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Button URL</Label>
                <Input value={block.button_url} onChange={(e) => updateBlock(block.id, { button_url: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Style</Label>
              <Select value={block.style} onValueChange={(v) => updateBlock(block.id, { style: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Primary Color</SelectItem>
                  <SelectItem value="secondary">Secondary Color</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 'team':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Section Title</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">Team Members</Label>
                <Button variant="outline" size="sm" onClick={() => updateBlock(block.id, { members: [...block.members, { id: uuidv4(), name: '', role: '', image: '', bio: '' }] })}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              {block.members.map((member, idx) => (
                <div key={member.id} className="p-3 border rounded-lg space-y-2 bg-slate-50">
                  <div className="flex justify-between">
                    <span className="text-xs text-slate-500">Member {idx + 1}</span>
                    <Button variant="ghost" size="sm" className="text-red-500 h-6" onClick={() => updateBlock(block.id, { members: block.members.filter(m => m.id !== member.id) })}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={member.name} onChange={(e) => {
                      const members = [...block.members];
                      members[idx] = { ...member, name: e.target.value };
                      updateBlock(block.id, { members });
                    }} placeholder="Name" />
                    <Input value={member.role} onChange={(e) => {
                      const members = [...block.members];
                      members[idx] = { ...member, role: e.target.value };
                      updateBlock(block.id, { members });
                    }} placeholder="Role" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Photo</Label>
                    <MediaSelector
                      value={member.image || ''}
                      onChange={(url) => {
                        const members = [...block.members];
                        members[idx] = { ...member, image: url };
                        updateBlock(block.id, { members });
                      }}
                      label="Team Photo"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'stats':
        return (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Title (optional)</Label>
              <Input value={block.title} onChange={(e) => updateBlock(block.id, { title: e.target.value })} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label className="text-xs">Statistics</Label>
                <Button variant="outline" size="sm" onClick={() => updateBlock(block.id, { items: [...block.items, { id: uuidv4(), value: '0', label: 'Label' }] })}>
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
              {block.items.map((item, idx) => (
                <div key={item.id} className="flex gap-2 items-center">
                  <Input value={item.value} onChange={(e) => {
                    const items = [...block.items];
                    items[idx] = { ...item, value: e.target.value };
                    updateBlock(block.id, { items });
                  }} placeholder="100+" className="w-24" />
                  <Input value={item.label} onChange={(e) => {
                    const items = [...block.items];
                    items[idx] = { ...item, label: e.target.value };
                    updateBlock(block.id, { items });
                  }} placeholder="Label" className="flex-1" />
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(block.id, { items: block.items.filter(i => i.id !== item.id) })}>
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'html':
        return (
          <div className="space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <strong>Tip:</strong> Paste your HTML code below. You can include inline styles or add CSS in the separate CSS field.
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-2">
                <Code size={14} />
                HTML Code
              </Label>
              <Textarea 
                value={block.html || ''} 
                onChange={(e) => updateBlock(block.id, { html: e.target.value })} 
                placeholder="<div>Your HTML here...</div>"
                rows={12}
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-2">
                <FileCode size={14} />
                CSS (optional)
              </Label>
              <Textarea 
                value={block.css || ''} 
                onChange={(e) => updateBlock(block.id, { css: e.target.value })} 
                placeholder=".my-class { color: red; }"
                rows={6}
                className="font-mono text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={block.full_width}
                onCheckedChange={(checked) => updateBlock(block.id, { full_width: checked })}
              />
              <Label className="text-xs">Full width (remove container padding)</Label>
            </div>
          </div>
        );

      default:
        return <p className="text-sm text-slate-500">Unknown block type</p>;
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-slate-500">Loading landing page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 min-w-0 p-8 overflow-auto">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Globe className="text-indigo-600" size={32} />
                Landing Page
              </h1>
              <p className="text-slate-500 mt-1">Create a one-page website for your business</p>
            </div>
            <div className="flex gap-3">
              {previewUrl && (
                <Button variant="outline" onClick={() => window.open(previewUrl, '_blank')}>
                  <Eye size={18} className="mr-2" />
                  Preview
                </Button>
              )}
              <Button onClick={handleSave} disabled={saving}>
                <Save size={18} className="mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>

          {/* Enable/Disable Toggle */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-900">Enable Landing Page</h3>
                  <p className="text-sm text-slate-500">Make your landing page publicly accessible</p>
                </div>
                <Switch
                  checked={landingPage.enabled}
                  onCheckedChange={(checked) => setLandingPage(prev => ({ ...prev, enabled: checked }))}
                />
              </div>
              {previewUrl && landingPage.enabled && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg flex items-center gap-3">
                  <Input value={previewUrl} readOnly className="flex-1 bg-white" />
                  <Button variant="outline" size="sm" onClick={copyPreviewUrl}>
                    {copied ? <Check size={16} /> : <Copy size={16} />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => window.open(previewUrl, '_blank')}>
                    <ExternalLink size={16} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Full HTML Mode Toggle */}
          <Card className="mb-6 border-amber-200 bg-amber-50/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-slate-900 flex items-center gap-2">
                    <Code size={18} className="text-amber-600" />
                    Full HTML Mode
                  </h3>
                  <p className="text-sm text-slate-500">Replace all sections with custom HTML/CSS code</p>
                </div>
                <Switch
                  checked={landingPage.use_html_mode || false}
                  onCheckedChange={(checked) => setLandingPage(prev => ({ 
                    ...prev, 
                    use_html_mode: checked,
                    html_sections: prev.html_sections || [{ id: uuidv4(), name: 'Section 1', anchor_id: 'section-1', html: '', css: '' }]
                  }))}
                />
              </div>
            </CardContent>
          </Card>

          {/* Conditional rendering based on HTML mode */}
          {landingPage.use_html_mode ? (
            <div className="space-y-6">
              {/* Navigation & Footer Tabs for HTML Mode */}
              <Tabs defaultValue="navigation" className="space-y-4">
                <TabsList className="grid grid-cols-3 w-full max-w-md">
                  <TabsTrigger value="navigation">Navigation</TabsTrigger>
                  <TabsTrigger value="footer">Footer</TabsTrigger>
                  <TabsTrigger value="html">HTML Sections</TabsTrigger>
                </TabsList>

                {/* Navigation Tab in HTML Mode */}
                <TabsContent value="navigation">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            <Menu size={20} />
                            Navigation Menu
                          </CardTitle>
                          <CardDescription>Add a navigation bar to your landing page</CardDescription>
                        </div>
                        <Switch
                          checked={landingPage.navigation?.enabled || false}
                          onCheckedChange={(checked) => updateNavigation('enabled', checked)}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-2">
                        <Label>Select Menu</Label>
                        <Select
                          value={landingPage.navigation?.menu_id || ''}
                          onValueChange={(value) => updateNavigation('menu_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a navigation menu" />
                          </SelectTrigger>
                          <SelectContent>
                            {menus.map((menu) => (
                              <SelectItem key={menu.menu_id} value={menu.menu_id}>
                                {menu.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {menus.length === 0 && (
                          <p className="text-sm text-slate-500">
                            No menus found. <a href="/navigation" className="text-indigo-600 hover:underline">Create a menu</a> first.
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Logo</Label>
                          <MediaSelector
                            value={landingPage.navigation?.logo_url || ''}
                            onChange={(url) => updateNavigation('logo_url', url)}
                            label="Navigation Logo"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Logo Text (if no image)</Label>
                          <Input
                            value={landingPage.navigation?.logo_text || ''}
                            onChange={(e) => updateNavigation('logo_text', e.target.value)}
                            placeholder="Your Company"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Background Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={landingPage.navigation?.background_color || '#4F46E5'}
                              onChange={(e) => updateNavigation('background_color', e.target.value)}
                              className="w-14 h-10 p-1"
                            />
                            <Input
                              value={landingPage.navigation?.background_color || ''}
                              onChange={(e) => updateNavigation('background_color', e.target.value)}
                              placeholder="#4F46E5"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Text Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={landingPage.navigation?.text_color || '#ffffff'}
                              onChange={(e) => updateNavigation('text_color', e.target.value)}
                              className="w-14 h-10 p-1"
                            />
                            <Input
                              value={landingPage.navigation?.text_color || ''}
                              onChange={(e) => updateNavigation('text_color', e.target.value)}
                              placeholder="#ffffff"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={landingPage.navigation?.sticky || false}
                            onCheckedChange={(checked) => updateNavigation('sticky', checked)}
                          />
                          <Label>Sticky Navigation</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={landingPage.navigation?.transparent || false}
                            onCheckedChange={(checked) => updateNavigation('transparent', checked)}
                          />
                          <Label>Transparent Background</Label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Footer Tab in HTML Mode */}
                <TabsContent value="footer">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>Footer</CardTitle>
                          <CardDescription>Configure your landing page footer</CardDescription>
                        </div>
                        <Switch
                          checked={landingPage.footer?.enabled !== false}
                          onCheckedChange={(checked) => updateFooter('enabled', checked)}
                        />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Footer Style */}
                      <div className="space-y-2">
                        <Label>Footer Style</Label>
                        <Select
                          value={landingPage.footer?.style || 'simple'}
                          onValueChange={(value) => updateFooter('style', value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="simple">Simple - Just copyright text</SelectItem>
                            <SelectItem value="centered">Centered - Logo, links, social</SelectItem>
                            <SelectItem value="columns">Columns - Multiple link sections</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Logo & Company Info */}
                      {landingPage.footer?.style !== 'simple' && (
                        <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-medium text-sm">Company Info</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs">Footer Logo</Label>
                              <MediaSelector
                                value={landingPage.footer?.logo_url || ''}
                                onChange={(url) => updateFooter('logo_url', url)}
                                label="Footer Logo"
                              />
                            </div>
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label className="text-xs">Company Name</Label>
                                <Input
                                  value={landingPage.footer?.company_name || ''}
                                  onChange={(e) => updateFooter('company_name', e.target.value)}
                                  placeholder="Your Company Name"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-xs">Tagline</Label>
                                <Input
                                  value={landingPage.footer?.tagline || ''}
                                  onChange={(e) => updateFooter('tagline', e.target.value)}
                                  placeholder="Your company tagline"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Copyright Text */}
                      <div className="space-y-2">
                        <Label>Copyright Text</Label>
                        <Input
                          value={landingPage.footer?.copyright || ''}
                          onChange={(e) => updateFooter('copyright', e.target.value)}
                          placeholder="© 2024 Your Company. All rights reserved."
                        />
                      </div>

                      {/* Social Links */}
                      <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-medium text-sm">Social Links</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <Facebook size={14} /> Facebook
                            </Label>
                            <Input
                              value={landingPage.footer?.social_links?.facebook || ''}
                              onChange={(e) => updateFooterSocialLinks('facebook', e.target.value)}
                              placeholder="https://facebook.com/..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <Twitter size={14} /> Twitter / X
                            </Label>
                            <Input
                              value={landingPage.footer?.social_links?.twitter || ''}
                              onChange={(e) => updateFooterSocialLinks('twitter', e.target.value)}
                              placeholder="https://twitter.com/..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <Instagram size={14} /> Instagram
                            </Label>
                            <Input
                              value={landingPage.footer?.social_links?.instagram || ''}
                              onChange={(e) => updateFooterSocialLinks('instagram', e.target.value)}
                              placeholder="https://instagram.com/..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <Linkedin size={14} /> LinkedIn
                            </Label>
                            <Input
                              value={landingPage.footer?.social_links?.linkedin || ''}
                              onChange={(e) => updateFooterSocialLinks('linkedin', e.target.value)}
                              placeholder="https://linkedin.com/..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <Youtube size={14} /> YouTube
                            </Label>
                            <Input
                              value={landingPage.footer?.social_links?.youtube || ''}
                              onChange={(e) => updateFooterSocialLinks('youtube', e.target.value)}
                              placeholder="https://youtube.com/..."
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <BlueskyIcon size={14} /> Bluesky
                            </Label>
                            <Input
                              value={landingPage.footer?.social_links?.bluesky || ''}
                              onChange={(e) => updateFooterSocialLinks('bluesky', e.target.value)}
                              placeholder="https://bsky.app/profile/..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Footer Colors */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Background Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={landingPage.footer?.background_color || '#4F46E5'}
                              onChange={(e) => updateFooter('background_color', e.target.value)}
                              className="w-14 h-10 p-1"
                            />
                            <Input
                              value={landingPage.footer?.background_color || ''}
                              onChange={(e) => updateFooter('background_color', e.target.value)}
                              placeholder="#4F46E5"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Text Color</Label>
                          <div className="flex gap-2">
                            <Input
                              type="color"
                              value={landingPage.footer?.text_color || '#ffffff'}
                              onChange={(e) => updateFooter('text_color', e.target.value)}
                              className="w-14 h-10 p-1"
                            />
                            <Input
                              value={landingPage.footer?.text_color || ''}
                              onChange={(e) => updateFooter('text_color', e.target.value)}
                              placeholder="#ffffff"
                            />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* HTML Sections Tab */}
                <TabsContent value="html">
              {/* Global CSS */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="text-amber-600" size={20} />
                    Global CSS Styles
                  </CardTitle>
                  <CardDescription>
                    CSS that applies to all HTML sections below
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={landingPage.custom_css || ''}
                    onChange={(e) => setLandingPage(prev => ({ ...prev, custom_css: e.target.value }))}
                    placeholder={`/* Global CSS styles */
body { font-family: 'Inter', sans-serif; }
.container { max-width: 1200px; margin: 0 auto; }`}
                    rows={8}
                    className="font-mono text-sm"
                  />
                </CardContent>
              </Card>

              {/* HTML Sections */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Code className="text-amber-600" size={20} />
                        HTML Sections
                      </CardTitle>
                      <CardDescription>
                        Add multiple HTML sections. Each renders in order from top to bottom.
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => setLandingPage(prev => {
                        const sectionNum = (prev.html_sections?.length || 0) + 1;
                        return {
                          ...prev,
                          html_sections: [
                            ...(prev.html_sections || []),
                            { id: uuidv4(), name: `Section ${sectionNum}`, anchor_id: `section-${sectionNum}`, html: '', css: '' }
                          ]
                        };
                      })}
                    >
                      <Plus size={18} className="mr-2" />
                      Add Section
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {(!landingPage.html_sections || landingPage.html_sections.length === 0) ? (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg">
                      <Code className="mx-auto mb-4 text-slate-300" size={48} />
                      <p className="text-slate-500 mb-4">No HTML sections added yet</p>
                      <Button 
                        variant="outline" 
                        onClick={() => setLandingPage(prev => ({
                          ...prev,
                          html_sections: [{ id: uuidv4(), name: 'Section 1', anchor_id: 'section-1', html: '', css: '' }]
                        }))}
                      >
                        <Plus size={16} className="mr-2" />
                        Add Your First Section
                      </Button>
                    </div>
                  ) : (
                    landingPage.html_sections.map((section, index) => (
                      <Card key={section.id} className={`border-slate-200 ${section.draft ? 'opacity-60 border-dashed border-amber-400 bg-amber-50/30' : ''}`}>
                        <CardHeader className={`py-3 ${section.draft ? 'bg-amber-50' : 'bg-slate-50'}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <GripVertical size={16} className="text-slate-400 cursor-grab" />
                              <div className="flex flex-col gap-1">
                                <Input
                                  value={section.name}
                                  onChange={(e) => {
                                    const sections = [...landingPage.html_sections];
                                    const newName = e.target.value;
                                    const anchorId = newName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                                    sections[index] = { ...section, name: newName, anchor_id: anchorId };
                                    setLandingPage(prev => ({ ...prev, html_sections: sections }));
                                  }}
                                  className="w-48 h-8 text-sm font-medium"
                                />
                                <span className="text-xs text-indigo-600 font-mono">
                                  #{section.anchor_id || section.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'section'}
                                </span>
                              </div>
                              {section.draft && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-amber-200 text-amber-800 rounded">
                                  DRAFT
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {/* Draft Toggle */}
                              <div className="flex items-center gap-1.5 mr-2 px-2 py-1 rounded bg-white border">
                                <Switch
                                  checked={!section.draft}
                                  onCheckedChange={(checked) => {
                                    const sections = [...landingPage.html_sections];
                                    sections[index] = { ...section, draft: !checked };
                                    setLandingPage(prev => ({ ...prev, html_sections: sections }));
                                  }}
                                />
                                <span className="text-xs text-slate-600">{section.draft ? 'Draft' : 'Live'}</span>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                disabled={index === 0}
                                onClick={() => {
                                  const sections = [...landingPage.html_sections];
                                  [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
                                  setLandingPage(prev => ({ ...prev, html_sections: sections }));
                                }}
                              >
                                <ChevronUp size={16} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                disabled={index === landingPage.html_sections.length - 1}
                                onClick={() => {
                                  const sections = [...landingPage.html_sections];
                                  [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
                                  setLandingPage(prev => ({ ...prev, html_sections: sections }));
                                }}
                              >
                                <ChevronDown size={16} />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-red-500 hover:text-red-700"
                                onClick={() => {
                                  setLandingPage(prev => ({
                                    ...prev,
                                    html_sections: prev.html_sections.filter(s => s.id !== section.id)
                                  }));
                                }}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-4 space-y-4">
                          {section.draft && (
                            <div className="p-3 bg-amber-100 border border-amber-300 rounded-lg text-sm text-amber-800">
                              <strong>Draft Mode:</strong> This section won&apos;t appear on the live preview until you toggle it to &quot;Live&quot;.
                            </div>
                          )}
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <FileCode size={14} />
                              HTML Code
                            </Label>
                            <Textarea
                              value={section.html || ''}
                              onChange={(e) => {
                                const sections = [...landingPage.html_sections];
                                sections[index] = { ...section, html: e.target.value };
                                setLandingPage(prev => ({ ...prev, html_sections: sections }));
                              }}
                              placeholder="<section>Your HTML here...</section>"
                              rows={12}
                              className="font-mono text-sm"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs flex items-center gap-2">
                              <Palette size={14} />
                              Section CSS (optional)
                            </Label>
                            <Textarea
                              value={section.css || ''}
                              onChange={(e) => {
                                const sections = [...landingPage.html_sections];
                                sections[index] = { ...section, css: e.target.value };
                                setLandingPage(prev => ({ ...prev, html_sections: sections }));
                              }}
                              placeholder="/* CSS specific to this section */"
                              rows={4}
                              className="font-mono text-sm"
                            />
                          </div>
                          
                          {/* Action buttons at bottom of each section */}
                          <div className="flex items-center justify-between pt-4 border-t mt-4">
                            <Button 
                              variant="outline"
                              onClick={() => setLandingPage(prev => {
                                const sectionNum = prev.html_sections.length + 1;
                                return {
                                  ...prev,
                                  html_sections: [
                                    ...prev.html_sections.slice(0, index + 1),
                                    { id: uuidv4(), name: `Section ${sectionNum}`, anchor_id: `section-${sectionNum}`, html: '', css: '', draft: false },
                                    ...prev.html_sections.slice(index + 1)
                                  ]
                                };
                              })}
                            >
                              <Plus size={16} className="mr-2" />
                              Add Section Below
                            </Button>
                            <Button onClick={handleSave} disabled={saving}>
                              <Save size={16} className="mr-2" />
                              {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
                </TabsContent>
              </Tabs>
            </div>
          ) : (
          <Tabs defaultValue="navigation" className="space-y-6">
            <TabsList className="grid grid-cols-8 w-full">
              <TabsTrigger value="navigation">Nav</TabsTrigger>
              <TabsTrigger value="hero">Hero</TabsTrigger>
              <TabsTrigger value="about">About</TabsTrigger>
              <TabsTrigger value="features">Features</TabsTrigger>
              <TabsTrigger value="blocks">Blocks</TabsTrigger>
              <TabsTrigger value="contact">Contact</TabsTrigger>
              <TabsTrigger value="footer">Footer</TabsTrigger>
              <TabsTrigger value="theme">Theme</TabsTrigger>
            </TabsList>

            {/* Navigation Section */}
            <TabsContent value="navigation">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Menu size={20} />
                        Navigation Menu
                      </CardTitle>
                      <CardDescription>Add a navigation bar to your landing page</CardDescription>
                    </div>
                    <Switch
                      checked={landingPage.navigation?.enabled || false}
                      onCheckedChange={(checked) => updateNavigation('enabled', checked)}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select Menu</Label>
                    <Select
                      value={landingPage.navigation?.menu_id || ''}
                      onValueChange={(value) => updateNavigation('menu_id', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a navigation menu" />
                      </SelectTrigger>
                      <SelectContent>
                        {menus.map((menu) => (
                          <SelectItem key={menu.menu_id} value={menu.menu_id}>
                            {menu.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {menus.length === 0 && (
                      <p className="text-sm text-slate-500">
                        No menus found. <a href="/navigation" className="text-indigo-600 hover:underline">Create a menu</a> first.
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Logo</Label>
                      <MediaSelector
                        value={landingPage.navigation?.logo_url || ''}
                        onChange={(url) => updateNavigation('logo_url', url)}
                        label="Navigation Logo"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Logo Text (if no image)</Label>
                      <Input
                        value={landingPage.navigation?.logo_text || ''}
                        onChange={(e) => updateNavigation('logo_text', e.target.value)}
                        placeholder="Your Company"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={landingPage.navigation?.sticky || false}
                        onCheckedChange={(checked) => updateNavigation('sticky', checked)}
                      />
                      <Label>Sticky Navigation</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={landingPage.navigation?.transparent || false}
                        onCheckedChange={(checked) => updateNavigation('transparent', checked)}
                      />
                      <Label>Transparent Background</Label>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Hero Section */}
            <TabsContent value="hero">
              <Card>
                <CardHeader>
                  <CardTitle>Hero Section</CardTitle>
                  <CardDescription>The first thing visitors see on your landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Headline</Label>
                    <Input value={landingPage.hero.headline} onChange={(e) => updateHero('headline', e.target.value)} placeholder="Your main headline" />
                  </div>
                  <div className="space-y-2">
                    <Label>Subheadline</Label>
                    <Textarea value={landingPage.hero.subheadline} onChange={(e) => updateHero('subheadline', e.target.value)} placeholder="A brief description..." rows={3} />
                  </div>
                  
                  {/* Hero Image */}
                  <div className="space-y-2">
                    <Label>Hero Image (Optional)</Label>
                    <MediaSelector
                      value={landingPage.hero.image_url || ''}
                      onChange={(url) => updateHero('image_url', url)}
                      label="Hero Image"
                    />
                  </div>
                  {landingPage.hero.image_url && (
                    <div className="space-y-2">
                      <Label>Image Position</Label>
                      <Select value={landingPage.hero.image_position || 'right'} onValueChange={(v) => updateHero('image_position', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="right">Right of Text</SelectItem>
                          <SelectItem value="left">Left of Text</SelectItem>
                          <SelectItem value="background">Background Image</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Call-to-Action Text</Label>
                      <Input value={landingPage.hero.cta_text} onChange={(e) => updateHero('cta_text', e.target.value)} placeholder="Get Started" />
                    </div>
                    <div className="space-y-2">
                      <Label>CTA Link</Label>
                      <Input value={landingPage.hero.cta_url} onChange={(e) => updateHero('cta_url', e.target.value)} placeholder="https://example.com, /signup or #contact" />
                      <p className="text-xs text-slate-500">External URL (https://...), an internal page (/signup), or an anchor (#contact).</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Background Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={landingPage.hero.background_color} onChange={(e) => updateHero('background_color', e.target.value)} className="w-14 h-10 p-1" />
                        <Input value={landingPage.hero.background_color} onChange={(e) => updateHero('background_color', e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Text Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={landingPage.hero.text_color} onChange={(e) => updateHero('text_color', e.target.value)} className="w-14 h-10 p-1" />
                        <Input value={landingPage.hero.text_color} onChange={(e) => updateHero('text_color', e.target.value)} />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* About Section */}
            <TabsContent value="about">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>About Section</CardTitle>
                      <CardDescription>Tell visitors about your business</CardDescription>
                    </div>
                    <Switch checked={landingPage.about.enabled} onCheckedChange={(checked) => updateAbout('enabled', checked)} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Section Title</Label>
                    <Input value={landingPage.about.title} onChange={(e) => updateAbout('title', e.target.value)} placeholder="About Us" />
                  </div>
                  <div className="space-y-2">
                    <Label>Content</Label>
                    <Textarea value={landingPage.about.content} onChange={(e) => updateAbout('content', e.target.value)} placeholder="Tell your story..." rows={5} />
                  </div>
                  <div className="space-y-2">
                    <Label>Image (optional)</Label>
                    <MediaSelector
                      value={landingPage.about.image_url || ''}
                      onChange={(url) => updateAbout('image_url', url)}
                      label="About Image"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Features Section */}
            <TabsContent value="features">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Features Section</CardTitle>
                      <CardDescription>Highlight what you offer</CardDescription>
                    </div>
                    <Switch checked={landingPage.features.enabled} onCheckedChange={(checked) => updateFeatures('enabled', checked)} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Section Title</Label>
                    <Input value={landingPage.features.title} onChange={(e) => updateFeatures('title', e.target.value)} placeholder="What We Offer" />
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Feature Items</Label>
                      <Button variant="outline" size="sm" onClick={addFeatureItem}>
                        <Plus size={16} className="mr-1" />
                        Add Feature
                      </Button>
                    </div>
                    
                    {landingPage.features.items.map((item, index) => (
                      <div key={item.id} className="p-4 border rounded-lg bg-slate-50 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-500">Feature {index + 1}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeFeatureItem(item.id)} className="text-red-500 hover:text-red-700">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Title</Label>
                            <Input value={item.title} onChange={(e) => updateFeatureItem(item.id, 'title', e.target.value)} placeholder="Feature title" />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Icon</Label>
                            <Input value={item.icon} onChange={(e) => updateFeatureItem(item.id, 'icon', e.target.value)} placeholder="star, zap, shield..." />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Description</Label>
                          <Textarea value={item.description} onChange={(e) => updateFeatureItem(item.id, 'description', e.target.value)} placeholder="Feature description..." rows={2} />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Custom Blocks Section */}
            <TabsContent value="blocks">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Custom Blocks</CardTitle>
                      <CardDescription>Add additional sections to your landing page</CardDescription>
                    </div>
                    <Button onClick={() => setShowAddBlockModal(true)}>
                      <Plus size={18} className="mr-2" />
                      Add Block
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {landingPage.blocks?.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg">
                      <Layout className="mx-auto mb-4 text-slate-300" size={48} />
                      <p className="text-slate-500 mb-4">No custom blocks added yet</p>
                      <Button variant="outline" onClick={() => setShowAddBlockModal(true)}>
                        <Plus size={16} className="mr-2" />
                        Add Your First Block
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {landingPage.blocks.map((block) => renderBlockEditor(block))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Contact Section */}
            <TabsContent value="contact">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Contact Section</CardTitle>
                      <CardDescription>Let visitors reach out to you</CardDescription>
                    </div>
                    <Switch checked={landingPage.contact.enabled} onCheckedChange={(checked) => updateContact('enabled', checked)} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Section Title</Label>
                    <Input value={landingPage.contact.title} onChange={(e) => updateContact('title', e.target.value)} placeholder="Get in Touch" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={landingPage.contact.email} onChange={(e) => updateContact('email', e.target.value)} placeholder="hello@example.com" />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input value={landingPage.contact.phone} onChange={(e) => updateContact('phone', e.target.value)} placeholder="+1 (555) 123-4567" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Textarea value={landingPage.contact.address} onChange={(e) => updateContact('address', e.target.value)} placeholder="123 Main St, City, State 12345" rows={2} />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={landingPage.contact.show_form} onCheckedChange={(checked) => updateContact('show_form', checked)} />
                    <Label>Show Contact Form</Label>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Footer Section */}
            <TabsContent value="footer">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Footer</CardTitle>
                      <CardDescription>Configure your landing page footer</CardDescription>
                    </div>
                    <Switch
                      checked={landingPage.footer?.enabled !== false}
                      onCheckedChange={(checked) => updateFooter('enabled', checked)}
                    />
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Footer Style */}
                  <div className="space-y-2">
                    <Label>Footer Style</Label>
                    <Select
                      value={landingPage.footer?.style || 'simple'}
                      onValueChange={(value) => updateFooter('style', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="simple">Simple - Just copyright text</SelectItem>
                        <SelectItem value="centered">Centered - Logo, links, social</SelectItem>
                        <SelectItem value="columns">Columns - Multiple link sections</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Logo & Company Info */}
                  {landingPage.footer?.style !== 'simple' && (
                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium text-sm">Company Info</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs">Footer Logo</Label>
                          <MediaSelector
                            value={landingPage.footer?.logo_url || ''}
                            onChange={(url) => updateFooter('logo_url', url)}
                            label="Footer Logo"
                          />
                        </div>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-xs">Company Name</Label>
                            <Input
                              value={landingPage.footer?.company_name || ''}
                              onChange={(e) => updateFooter('company_name', e.target.value)}
                              placeholder="Your Company Name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-xs">Tagline</Label>
                            <Input
                              value={landingPage.footer?.tagline || ''}
                              onChange={(e) => updateFooter('tagline', e.target.value)}
                              placeholder="Your company tagline"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Copyright Text */}
                  <div className="space-y-2">
                    <Label>Copyright Text</Label>
                    <Input
                      value={landingPage.footer?.copyright || ''}
                      onChange={(e) => updateFooter('copyright', e.target.value)}
                      placeholder="© 2024 Your Company. All rights reserved."
                    />
                  </div>

                  {/* Social Links */}
                  <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium text-sm">Social Links</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs flex items-center gap-2">
                          <Facebook size={14} /> Facebook
                        </Label>
                        <Input
                          value={landingPage.footer?.social_links?.facebook || ''}
                          onChange={(e) => updateFooterSocialLinks('facebook', e.target.value)}
                          placeholder="https://facebook.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs flex items-center gap-2">
                          <Twitter size={14} /> Twitter / X
                        </Label>
                        <Input
                          value={landingPage.footer?.social_links?.twitter || ''}
                          onChange={(e) => updateFooterSocialLinks('twitter', e.target.value)}
                          placeholder="https://twitter.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs flex items-center gap-2">
                          <Instagram size={14} /> Instagram
                        </Label>
                        <Input
                          value={landingPage.footer?.social_links?.instagram || ''}
                          onChange={(e) => updateFooterSocialLinks('instagram', e.target.value)}
                          placeholder="https://instagram.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs flex items-center gap-2">
                          <Linkedin size={14} /> LinkedIn
                        </Label>
                        <Input
                          value={landingPage.footer?.social_links?.linkedin || ''}
                          onChange={(e) => updateFooterSocialLinks('linkedin', e.target.value)}
                          placeholder="https://linkedin.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs flex items-center gap-2">
                          <Youtube size={14} /> YouTube
                        </Label>
                        <Input
                          value={landingPage.footer?.social_links?.youtube || ''}
                          onChange={(e) => updateFooterSocialLinks('youtube', e.target.value)}
                          placeholder="https://youtube.com/..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs flex items-center gap-2">
                          <BlueskyIcon size={14} /> Bluesky
                        </Label>
                        <Input
                          value={landingPage.footer?.social_links?.bluesky || ''}
                          onChange={(e) => updateFooterSocialLinks('bluesky', e.target.value)}
                          placeholder="https://bsky.app/profile/..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Info in Footer */}
                  {landingPage.footer?.style !== 'simple' && (
                    <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium text-sm">Contact Info in Footer</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs flex items-center gap-2">
                            <Mail size={14} /> Email
                          </Label>
                          <Input
                            value={landingPage.footer?.contact_info?.email || ''}
                            onChange={(e) => updateFooterContactInfo('email', e.target.value)}
                            placeholder="hello@example.com"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs flex items-center gap-2">
                            <Phone size={14} /> Phone
                          </Label>
                          <Input
                            value={landingPage.footer?.contact_info?.phone || ''}
                            onChange={(e) => updateFooterContactInfo('phone', e.target.value)}
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs flex items-center gap-2">
                            <MapPin size={14} /> Address
                          </Label>
                          <Input
                            value={landingPage.footer?.contact_info?.address || ''}
                            onChange={(e) => updateFooterContactInfo('address', e.target.value)}
                            placeholder="123 Main St, City"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Footer Colors */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Background Color (optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={landingPage.footer?.background_color || landingPage.hero?.background_color || '#4F46E5'}
                          onChange={(e) => updateFooter('background_color', e.target.value)}
                          className="w-14 h-10 p-1"
                        />
                        <Input
                          value={landingPage.footer?.background_color || ''}
                          onChange={(e) => updateFooter('background_color', e.target.value)}
                          placeholder="Use hero color"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Text Color (optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={landingPage.footer?.text_color || '#ffffff'}
                          onChange={(e) => updateFooter('text_color', e.target.value)}
                          className="w-14 h-10 p-1"
                        />
                        <Input
                          value={landingPage.footer?.text_color || ''}
                          onChange={(e) => updateFooter('text_color', e.target.value)}
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Theme Section */}
            <TabsContent value="theme">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette size={20} />
                    Theme Settings
                  </CardTitle>
                  <CardDescription>Customize the look and feel of your landing page</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={landingPage.theme.primary_color} onChange={(e) => updateTheme('primary_color', e.target.value)} className="w-14 h-10 p-1" />
                        <Input value={landingPage.theme.primary_color} onChange={(e) => updateTheme('primary_color', e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={landingPage.theme.secondary_color} onChange={(e) => updateTheme('secondary_color', e.target.value)} className="w-14 h-10 p-1" />
                        <Input value={landingPage.theme.secondary_color} onChange={(e) => updateTheme('secondary_color', e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Background Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={landingPage.theme.background_color} onChange={(e) => updateTheme('background_color', e.target.value)} className="w-14 h-10 p-1" />
                        <Input value={landingPage.theme.background_color} onChange={(e) => updateTheme('background_color', e.target.value)} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Text Color</Label>
                      <div className="flex gap-2">
                        <Input type="color" value={landingPage.theme.text_color} onChange={(e) => updateTheme('text_color', e.target.value)} className="w-14 h-10 p-1" />
                        <Input value={landingPage.theme.text_color} onChange={(e) => updateTheme('text_color', e.target.value)} />
                      </div>
                    </div>
                  </div>

                  {/* Preview box */}
                  <div className="p-6 rounded-lg mt-4" style={{ backgroundColor: landingPage.theme.background_color, color: landingPage.theme.text_color }}>
                    <h3 className="text-xl font-bold mb-2">Preview</h3>
                    <p className="mb-4">This is how your text will look on the background.</p>
                    <button className="px-4 py-2 rounded font-medium text-white" style={{ backgroundColor: landingPage.theme.primary_color }}>Primary Button</button>
                    <button className="px-4 py-2 rounded font-medium text-white ml-2" style={{ backgroundColor: landingPage.theme.secondary_color }}>Secondary Button</button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
          )}
        </div>
      </div>

      {/* Add Block Modal */}
      <Dialog open={showAddBlockModal} onOpenChange={setShowAddBlockModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Block</DialogTitle>
            <DialogDescription>Choose a block type to add to your landing page</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4 max-h-96 overflow-y-auto">
            {blockTypes.map((block) => {
              const Icon = block.icon;
              return (
                <button
                  key={block.type}
                  onClick={() => addBlock(block.type)}
                  className="flex items-start gap-3 p-4 border rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-left"
                >
                  <div className="p-2 rounded-lg bg-indigo-100">
                    <Icon size={20} className="text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">{block.name}</h4>
                    <p className="text-sm text-slate-500">{block.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TenantLandingPage;
